import {
  type Campaign,
  CONTENT_KINDS,
  type ContentKind,
  FUNNEL_STAGES,
  type FunnelStage,
} from '../api.ts';
import { type BoardCard, needsYou } from './model.ts';

/**
 * What the board's toolbar narrows it to (US-016), read from and written to
 * the address (Design reference § URL parameters) the way `Contents.tsx`
 * does: every default is left out, so a plain `/board` is the whole board.
 */

/** The **Show** segment: everything, or one reason to look at a card. */
export type BoardShow = 'all' | 'attention' | 'blocked' | 'due';

export const BOARD_SHOWS: readonly { readonly value: BoardShow; readonly label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'attention', label: 'Needs you' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'due', label: 'Due this week' },
];

export interface BoardFilters {
  /** A campaign id, `none` for cards without one, or `all`. */
  readonly campaign: string;
  /** A brand book id, or `all`. */
  readonly book: string;
  readonly kind: ContentKind | 'all';
  readonly funnel: FunnelStage | 'all';
  readonly show: BoardShow;
  /** The search as typed into the address; trimmed. */
  readonly q: string;
  /** `posted=all`: every posted content, not only the last 30 days'. */
  readonly postedAll: boolean;
  /** `archived=1`: archived contents too. */
  readonly archived: boolean;
}

export function isShow(value: string | null): value is BoardShow {
  return BOARD_SHOWS.some((show) => show.value === value);
}

export function isKind(value: string | null): value is ContentKind {
  return CONTENT_KINDS.some((kind) => kind === value);
}

export function isFunnelStage(value: string | null): value is FunnelStage {
  return FUNNEL_STAGES.some((stage) => stage === value);
}

/**
 * The filters an address asks for. A campaign or brand book id is only
 * checked against the lists once they are known (`null` before the first
 * read), so a bookmarked view does not lose its campaign while the board loads.
 */
export function readBoardFilters(
  params: URLSearchParams,
  campaigns: readonly Pick<Campaign, 'id'>[] | null,
  brandBooks: readonly { readonly id: string }[] | null,
): BoardFilters {
  const campaign = params.get('campaign');
  const book = params.get('book');
  const kind = params.get('kind');
  const funnel = params.get('funnel');
  const show = params.get('show');
  return {
    campaign:
      campaign !== null &&
      (campaign === 'none' || campaigns === null || campaigns.some((one) => one.id === campaign))
        ? campaign
        : 'all',
    book:
      book !== null && (brandBooks === null || brandBooks.some((one) => one.id === book))
        ? book
        : 'all',
    kind: isKind(kind) ? kind : 'all',
    funnel: isFunnelStage(funnel) ? funnel : 'all',
    show: isShow(show) ? show : 'all',
    q: (params.get('q') ?? '').trim(),
    postedAll: params.get('posted') === 'all',
    archived: params.get('archived') === '1',
  };
}

/** Whether a filter narrows the board at all; `posted`/`archived` widen it. */
export function filtersNarrow(filters: BoardFilters): boolean {
  return (
    filters.campaign !== 'all' ||
    filters.book !== 'all' ||
    filters.kind !== 'all' ||
    filters.funnel !== 'all' ||
    filters.show !== 'all' ||
    filters.q !== ''
  );
}

/** The parameters the filters own; **Clear filters** drops every one of them. */
export const FILTER_PARAMS = ['campaign', 'book', 'kind', 'funnel', 'show', 'q', 'posted', 'archived'] as const;

/** The local `YYYY-MM-DD` of a moment, so a `dueOn` day compares as text. */
function localDay(moment: number): string {
  const date = new Date(moment);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today plus `days`, as a local `YYYY-MM-DD` (safe across a clock change). */
function dayAhead(now: number, days: number): string {
  const date = new Date(now);
  date.setDate(date.getDate() + days);
  return localDay(date.getTime());
}

/** The texts the search box looks through. */
function searchTexts(card: BoardCard, campaigns: ReadonlyMap<string, Campaign>): (string | null)[] {
  const item = card.type === 'idea' ? card.idea : card.content;
  const details = item.briefDetails;
  const campaign = item.campaignId === null ? null : (campaigns.get(item.campaignId)?.name ?? null);
  return [
    card.type === 'idea' ? card.idea.title : null,
    card.type === 'idea' ? card.idea.sourceTitle : null,
    card.type === 'idea' ? card.idea.seed : null,
    card.type === 'content' ? card.content.name : null,
    item.brief,
    campaign,
    details.goal,
    details.audience,
    details.angle,
    details.placement,
    ...details.extraDetails.map((detail) => detail.value),
  ];
}

/**
 * "Due this week": a publish-by date at most 7 days from today, or already
 * past, on a card that has not been placed.
 */
export function dueThisWeek(card: BoardCard, now: number): boolean {
  const dueOn = card.type === 'idea' ? card.idea.dueOn : card.content.dueOn;
  if (dueOn === null) return false;
  if (card.type === 'content' && card.content.briefDetails.placed) return false;
  return dueOn <= dayAhead(now, 7);
}

/** The cards the filters keep, in the order they came. */
export function filterCards(
  cards: readonly BoardCard[],
  filters: BoardFilters,
  campaigns: ReadonlyMap<string, Campaign>,
  now: number,
): BoardCard[] {
  const needle = filters.q.toLowerCase();
  return cards.filter((card) => {
    const item = card.type === 'idea' ? card.idea : card.content;
    if (filters.campaign === 'none' && item.campaignId !== null) return false;
    if (filters.campaign !== 'all' && filters.campaign !== 'none' && item.campaignId !== filters.campaign) {
      return false;
    }
    if (filters.book !== 'all') {
      const book = card.type === 'idea' ? card.batch.brandBookId : (card.content.brandBook?.id ?? null);
      if (book !== filters.book) return false;
    }
    if (filters.kind !== 'all' && item.kind !== filters.kind) return false;
    if (filters.funnel !== 'all' && !item.briefDetails.funnelStages.includes(filters.funnel)) return false;
    if (filters.show === 'attention' && !needsYou(card)) return false;
    if (filters.show === 'blocked' && item.blockedReason === null) return false;
    if (filters.show === 'due' && !dueThisWeek(card, now)) return false;
    return (
      needle === '' ||
      searchTexts(card, campaigns).some((text) => text !== null && text.toLowerCase().includes(needle))
    );
  });
}
